import { useCallback, useEffect, useState } from 'react';

/** Describes one toggleable column in an HQ table. */
export interface ColumnDef {
  /** Stable identifier persisted to localStorage — never rename without a migration. */
  key: string;
  /** Label shown in the column-visibility menu. */
  label: string;
  /** Column is always shown and omitted from the menu (e.g. the row's primary identifier). */
  required?: boolean;
}

/**
 * Persists which columns of an HQ table are shown, keyed by `storageKey`, to
 * localStorage. Falls back to `defaultHidden` when storage is empty, holds an
 * unparseable value, or is unavailable (private browsing, blocked site data) —
 * every read/write is wrapped so a storage failure never breaks the table.
 *
 * `columns` should be a stable (module-level or memoized) array — it's only
 * read on mount and inside `toggle`.
 *
 * @param storageKey - localStorage key unique to this table (e.g. 'hq-insights-columns').
 * @param columns - The full set of columns the table can render.
 * @param defaultHidden - Column keys hidden out of the box, before any preference is saved.
 */
export function useColumnVisibility(storageKey: string, columns: ColumnDef[], defaultHidden: string[] = []) {
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          const validKeys = new Set(columns.map((c) => c.key));
          return new Set(parsed.filter((k) => typeof k === 'string' && validKeys.has(k)));
        }
      }
    } catch {
      // Corrupt JSON or storage inaccessible — fall through to defaults.
    }
    return new Set(defaultHidden);
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(Array.from(hidden)));
    } catch {
      // Private browsing / quota exceeded — the preference just won't persist.
    }
  }, [storageKey, hidden]);

  const isVisible = useCallback((key: string) => !hidden.has(key), [hidden]);

  const toggle = useCallback(
    (key: string) => {
      if (columns.find((c) => c.key === key)?.required) return;
      setHidden((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [columns]
  );

  const resetToDefault = useCallback(() => setHidden(new Set(defaultHidden)), [defaultHidden]);

  return { isVisible, toggle, resetToDefault };
}
